'use client';

import { useCallback, useLayoutEffect, useState } from 'react';
import type { Boundary, Placement, ReferenceElement } from '@floating-ui/dom';
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  hide,
  offset,
  shift,
} from '@floating-ui/dom';

export type UseFloatingPositionProps = {
  /** Whether the positioning logic is enabled */
  enabled?: boolean;
  /** Offset in pixels along the main axis (e.g., vertical offset for top/bottom placements) */
  offsetMainAxis?: number;
  /** Offset in pixels along the cross axis (e.g., horizontal offset for top/bottom placements) */
  offsetCrossAxis?: number;
  /** Preferred placement of the tooltip relative to the target element */
  placement?: Placement;
  /** Reference to the arrow element for positioning calculations */
  arrowRef?: React.RefObject<HTMLElement>;
  /** Boundary element or area that the tooltip should stay within */
  boundary?: Boundary;
  /** Whether the tooltip should be hidden when the target element is hidden */
  autoHide?: boolean;
  /** Fallback placements to try if the preferred placement is not available */
  fallbackPlacements?: Placement[];
  /** Padding to add to the tooltip to account for overflow */
  overflowPadding?: number;
};

export function useFloatingPosition<T extends HTMLElement>(
  targetElement?: ReferenceElement,
  tooltipRef?: React.RefObject<T>,
  {
    enabled = true,
    offsetMainAxis = 0,
    offsetCrossAxis = 0,
    placement = 'top',
    arrowRef,
    boundary,
    autoHide = true,
    fallbackPlacements,
    overflowPadding = 8,
  }: UseFloatingPositionProps = {}
) {
  const [resolvedPlacement, setResolvedPlacement] = useState<Placement>();
  const [referenceHidden, setReferenceHidden] = useState(false);

  const updatePosition = useCallback(async () => {
    if (!tooltipRef?.current || !targetElement) {
      return;
    }

    const tooltip = tooltipRef.current;
    const target = targetElement;

    const middlewareStack = [
      // Applies offset to the tooltip position along main and cross axes
      offset({ mainAxis: offsetMainAxis, crossAxis: offsetCrossAxis }),

      // Changes placement when tooltip would overflow the boundary
      // Falls back to alternative placements if the preferred one doesn't fit
      flip({
        boundary,
        fallbackPlacements,
      }),

      // Shifts the tooltip to keep it within the boundary when it would overflow
      shift({ boundary, crossAxis: true, padding: overflowPadding }),

      // Hides the tooltip when the reference element is no longer visible
      autoHide && hide({ strategy: 'referenceHidden' }),

      // Positions the arrow element to point at the reference element
      // Only included if an arrow reference exists
      arrowRef?.current && arrow({ element: arrowRef.current, padding: 2 }),
    ].filter(Boolean); // Removes any falsy values from the middleware array

    const {
      x,
      y,
      middlewareData,
      placement: newPlacement,
    } = await computePosition(target, tooltip, {
      placement,
      middleware: middlewareStack,
    });

    // Update tooltip position and visibility
    const tooltipStyles = {
      position: 'absolute',
      left: `${x}px`,
      top: `${y}px`,
    };

    Object.assign(tooltip.style, tooltipStyles);

    // Update placement state
    setResolvedPlacement(newPlacement);
    setReferenceHidden(!!middlewareData.hide?.referenceHidden);

    // Update arrow position if it exists
    if (arrowRef?.current && middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;
      const arrowStyles = {
        left: arrowX != null ? `${arrowX}px` : '',
        top: arrowY != null ? `${arrowY}px` : '',
      };
      Object.assign(arrowRef.current.style, arrowStyles);

      // Mark the arrow as positioned by floating-ui
      arrowRef.current.setAttribute('data-floating-ui-positioned', '');
    }
  }, [
    arrowRef,
    boundary,
    offsetMainAxis,
    offsetCrossAxis,
    placement,
    targetElement,
    tooltipRef,
    autoHide,
    fallbackPlacements,
    overflowPadding,
  ]);

  useLayoutEffect(() => {
    if (enabled && tooltipRef?.current && targetElement) {
      return autoUpdate(targetElement, tooltipRef.current, updatePosition);
    }

    return undefined;
  }, [
    arrowRef,
    boundary,
    enabled,
    offsetMainAxis,
    offsetCrossAxis,
    placement,
    targetElement,
    tooltipRef,
    updatePosition,
    overflowPadding,
  ]);

  return { placement: resolvedPlacement, referenceHidden, updatePosition };
}
